import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { supabase } from "@/lib/supabase";
import { applyAppearance } from "@/lib/appearance";
import { applySeo } from "@/lib/seo";
import type { Project, Service, Technology, SocialLink } from "@/lib/types";

type Settings = Record<string, string | number | boolean>;
type Sections = Record<string, Record<string, unknown>>;

interface SiteContentValue {
  loading: boolean;
  error: string | null;
  settings: Settings;
  sections: Sections;
  services: Service[];
  projects: Project[];
  technologies: Technology[];
  socialLinks: SocialLink[];
  refresh: () => void;
}

const SiteContentContext = createContext<SiteContentValue | undefined>(undefined);

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<Settings>({});
  const [sections, setSections] = useState<Sections>({});
  const [services, setServices] = useState<Service[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [settingsRes, sectionsRes, servicesRes, projectsRes, techRes, socialRes] =
        await Promise.all([
          supabase.from("site_settings").select("key, value"),
          supabase.from("content_sections").select("seccion, clave, valor"),
          supabase.from("services").select("*").order("orden"),
          supabase.from("projects").select("*").order("orden"),
          supabase.from("technologies").select("*").order("orden"),
          supabase.from("social_links").select("*").order("orden"),
        ]);

      if (settingsRes.error) throw settingsRes.error;
      if (sectionsRes.error) throw sectionsRes.error;

      const newSettings: Settings = {};
      (settingsRes.data ?? []).forEach((row) => {
        newSettings[row.key] = row.value as string | number | boolean;
      });

      const newSections: Sections = {};
      (sectionsRes.data ?? []).forEach((row) => {
        newSections[row.seccion] = (row.valor as Record<string, unknown>) ?? {};
      });

      setSettings(newSettings);
      setSections(newSections);
      setServices((servicesRes.data ?? []) as Service[]);
      setProjects((projectsRes.data ?? []) as Project[]);
      setTechnologies((techRes.data ?? []) as Technology[]);
      setSocialLinks((socialRes.data ?? []) as SocialLink[]);

      applyAppearance(newSettings);
      applySeo(newSettings);
    } catch {
      setError("No se pudieron cargar los contenidos del sitio.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <SiteContentContext.Provider
      value={{
        loading,
        error,
        settings,
        sections,
        services,
        projects,
        technologies,
        socialLinks,
        refresh: load,
      }}
    >
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  if (!ctx) {
    throw new Error("useSiteContent debe usarse dentro de SiteContentProvider");
  }
  return ctx;
}